import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NewsCard from './components/NewsCard';
import EventCard from './components/EventCard';
import FilterToolbar from './components/FilterToolbar';
import SavedSearches from './components/SavedSearches';
import AlertSettings from './components/AlertSettings';

const NewsAndEvents = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    market: 'all',
    impact: 'all',
    source: 'all',
    timeRange: '24h',
    contentType: 'all'
  });
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [selectedAlertSettings, setSelectedAlertSettings] = useState(null);

  // Mock news data
  const mockNews = [
    {
      id: 1,
      headline: "EU Carbon Prices Surge 12% Following New Climate Policy Announcement",
      summary: `European Union Allowance (EUA) prices jumped to €89.50 per tonne after the European Commission announced stricter emissions targets for 2025. The policy change affects over 10,000 installations across the EU, with immediate implications for power generation and industrial sectors.`,
      source: "Reuters",
      publishedAt: new Date(Date.now() - 1800000), // 30 minutes ago
      impactScore: 9,
      relevantMarkets: ["EUA"],
      sentiment: "positive",
      marketCorrelation: 87,
      isBookmarked: false
    },
    {
      id: 2,
      headline: "Brent Crude Oil Futures Drop 3% on OPEC+ Production Increase Rumors",
      summary: `Oil futures declined sharply in early trading as reports suggest OPEC+ members are considering increasing production quotas by 500,000 barrels per day. The move comes amid growing concerns about global economic slowdown and reduced energy demand.`,
      source: "Bloomberg",
      publishedAt: new Date(Date.now() - 3600000), // 1 hour ago
      impactScore: 8,
      relevantMarkets: ["Oil"],
      sentiment: "negative",
      marketCorrelation: 92,
      isBookmarked: true
    },
    {
      id: 3,
      headline: "German Industrial Output Falls 2.1%, Impacting EUA Demand Forecasts",
      summary: `Germany's industrial production declined more than expected in the latest monthly data, raising questions about future carbon allowance demand from the manufacturing sector. Analysts are revising EUA price targets downward for Q4 2024.`,
      source: "Financial Times",
      publishedAt: new Date(Date.now() - 7200000), // 2 hours ago
      impactScore: 6,
      relevantMarkets: ["EUA"],
      sentiment: "negative",
      marketCorrelation: 74,
      isBookmarked: false
    },
    {
      id: 4,
      headline: "Middle East Tensions Rise: Oil Supply Chain Disruption Concerns Mount",
      summary: `Geopolitical tensions in the Middle East have escalated following diplomatic disputes between major oil-producing nations. Shipping companies report increased insurance costs for tankers transiting key maritime routes, potentially affecting global oil supply chains.`,
      source: "Wall Street Journal",
      publishedAt: new Date(Date.now() - 10800000), // 3 hours ago
      impactScore: 7,
      relevantMarkets: ["Oil"],
      sentiment: "negative",
      marketCorrelation: 85,
      isBookmarked: false
    },
    {
      id: 5,
      headline: "UK Announces £2B Green Investment Fund for Carbon Capture Technology",
      summary: `The UK government unveiled a major investment initiative focused on carbon capture and storage technologies. The fund aims to support industrial decarbonization efforts and could influence future EUA allocation methodologies across Europe.`,
      source: "S&P Global Platts",
      publishedAt: new Date(Date.now() - 14400000), // 4 hours ago
      impactScore: 5,
      relevantMarkets: ["EUA"],
      sentiment: "positive",
      marketCorrelation: 68,
      isBookmarked: true
    }
  ];

  // Mock events data
  const mockEvents = [
    {
      id: 1,
      title: "EU ETS Phase 5 Consultation Deadline",
      description: "Final deadline for stakeholder submissions on the European Union Emissions Trading System Phase 5 design. Responses will influence allocation methodologies and free allowance distribution for 2026-2030.",
      type: "regulatory",
      date: new Date(Date.now() + 86400000 * 3), // 3 days from now
      relevantMarkets: ["EUA"],
      priority: true,
      hasReminder: false
    },
    {
      id: 2,
      title: "OPEC+ Monthly Production Meeting",
      description: "Organization of Petroleum Exporting Countries and allies will review current production quotas and discuss potential adjustments based on global demand forecasts and market conditions.",
      type: "market",
      date: new Date(Date.now() + 86400000 * 7), // 1 week from now
      relevantMarkets: ["Oil"],
      priority: false,
      hasReminder: true
    },
    {
      id: 3,
      title: "Shell Q4 2024 Earnings Release",
      description: "Royal Dutch Shell will report quarterly earnings with focus on upstream production, refining margins, and renewable energy investments. Results expected to impact oil sector sentiment.",
      type: "earnings",
      date: new Date(Date.now() + 86400000 * 14), // 2 weeks from now
      relevantMarkets: ["Oil"],
      priority: false,
      hasReminder: false
    },
    {
      id: 4,
      title: "European Green Deal Progress Review",
      description: "European Commission will publish comprehensive review of Green Deal implementation progress, including updates on carbon neutrality targets and potential policy adjustments.",
      type: "policy",
      date: new Date(Date.now() + 86400000 * 21), // 3 weeks from now
      relevantMarkets: ["EUA"],
      priority: true,
      hasReminder: true
    },
    {
      id: 5,
      title: "IEA Monthly Oil Market Report",
      description: "International Energy Agency will release monthly analysis of global oil supply, demand, and inventory levels with updated forecasts for 2025.",
      type: "market",
      date: new Date(Date.now() + 86400000 * 28), // 4 weeks from now
      relevantMarkets: ["Oil"],
      priority: false,
      hasReminder: false
    }
  ];

  // Mock saved searches
  const [savedSearches, setSavedSearches] = useState([
    {
      id: 1,
      name: "High Impact EUA News",
      description: "EUA-related news with high impact scores from major financial sources",
      query: "EUA carbon allowance",
      filters: { market: 'eua', impact: 'high', source: 'all', timeRange: '24h', contentType: 'news' },
      createdAt: new Date(Date.now() - 86400000 * 7),
      usageCount: 23
    },
    {
      id: 2,
      name: "Oil Supply Chain Alerts",
      description: "Oil futures news focusing on supply chain and geopolitical events",
      query: "supply chain disruption OPEC",
      filters: { market: 'oil', impact: 'medium', source: 'all', timeRange: '7d', contentType: 'all' },
      createdAt: new Date(Date.now() - 86400000 * 14),
      usageCount: 45
    },
    {
      id: 3,
      name: "Regulatory Deadlines",
      description: "All regulatory events and compliance deadlines",
      query: "",
      filters: { market: 'all', impact: 'all', source: 'all', timeRange: '30d', contentType: 'events' },
      createdAt: new Date(Date.now() - 86400000 * 21),
      usageCount: 12
    }
  ]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      market: 'all',
      impact: 'all',
      source: 'all',
      timeRange: '24h',
      contentType: 'all'
    });
    setSearchQuery('');
  };

  const handleSaveSearch = () => {
    if (searchQuery?.trim() === '') {
      alert('Please enter a search query to save');
      return;
    }

    const newSearch = {
      id: Date.now(),
      name: `Search: ${searchQuery?.substring(0, 30)}${searchQuery?.length > 30 ? '...' : ''}`,
      description: `Custom search for "${searchQuery}" with current filters`,
      query: searchQuery,
      filters: { ...filters },
      createdAt: new Date(),
      usageCount: 0
    };

    setSavedSearches(prev => [newSearch, ...prev]);
    alert('Search saved successfully!');
  };

  const handleLoadSearch = (search) => {
    setSearchQuery(search?.query);
    setFilters(search?.filters);
    setSavedSearches(prev => 
      prev?.map(s => s?.id === search?.id ? { ...s, usageCount: s?.usageCount + 1 } : s)
    );
    setShowSavedSearches(false);
  };

  const handleDeleteSearch = (searchId) => {
    if (confirm('Are you sure you want to delete this saved search?')) {
      setSavedSearches(prev => prev?.filter(s => s?.id !== searchId));
    }
  };

  const handleCreateAlert = (search = null) => {
    setSelectedAlertSettings(search ? {
      name: `Alert: ${search?.name}`,
      keywords: search?.query,
      markets: [search?.filters?.market],
      impactLevel: search?.filters?.impact,
      sources: [search?.filters?.source],
      frequency: 'immediate',
      channels: ['email'],
      isActive: true
    } : null);
    setShowAlertSettings(true);
  };

  const handleSaveAlert = (alertSettings) => {
    console.log('Saving alert:', alertSettings);
    alert('Alert saved successfully!');
  };

  const handleBookmark = (articleId) => {
    // Mock bookmark functionality
    console.log('Bookmarking article:', articleId);
  };

  const handleShare = (article) => {
    // Mock share functionality
    console.log('Sharing article:', article?.headline);
    alert('Article link copied to clipboard!');
  };

  const handleViewDetails = (item) => {
    // Mock view details functionality
    console.log('Viewing details for:', item?.headline || item?.title);
    alert('Opening detailed view...');
  };

  const handleSetReminder = (eventId) => {
    // Mock reminder functionality
    console.log('Setting reminder for event:', eventId);
    alert('Reminder set successfully!');
  };

  const filteredNews = mockNews?.filter(article => {
    if (searchQuery && !article?.headline?.toLowerCase()?.includes(searchQuery?.toLowerCase()) &&
        !article?.summary?.toLowerCase()?.includes(searchQuery?.toLowerCase())) {
      return false;
    }
    if (filters?.market !== 'all' && !article?.relevantMarkets?.some(market => 
        market?.toLowerCase() === filters?.market)) {
      return false;
    }
    if (filters?.impact !== 'all') {
      const impactRanges = {
        high: [8, 10],
        medium: [6, 7],
        low: [4, 5],
        minimal: [1, 3]
      };
      const [min, max] = impactRanges?.[filters?.impact];
      if (article?.impactScore < min || article?.impactScore > max) {
        return false;
      }
    }
    return true;
  });

  const filteredEvents = mockEvents?.filter(event => {
    if (searchQuery && !event?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) &&
        !event?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())) {
      return false;
    }
    if (filters?.market !== 'all' && !event?.relevantMarkets?.some(market => 
        market?.toLowerCase() === filters?.market)) {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">News & Events</h1>
              <p className="text-muted-foreground">
                Real-time market intelligence and regulatory updates for EUA & Oil Futures trading
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSavedSearches(!showSavedSearches)}
                className="flex items-center gap-2"
              >
                <Icon name="BookmarkCheck" size={16} />
                <span>Saved Searches</span>
              </Button>
              <Button
                variant="default"
                onClick={() => handleCreateAlert()}
                className="flex items-center gap-2"
              >
                <Icon name="Bell" size={16} />
                <span>Create Alert</span>
              </Button>
            </div>
          </div>

          {/* Saved Searches Section */}
          {showSavedSearches && (
            <SavedSearches
              savedSearches={savedSearches}
              onLoadSearch={handleLoadSearch}
              onDeleteSearch={handleDeleteSearch}
              onCreateAlert={handleCreateAlert}
            />
          )}

          {/* Filter Toolbar */}
          <FilterToolbar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onSaveSearch={handleSaveSearch}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />

          {/* Content Tabs */}
          <div className="bg-card border border-border rounded-lg mb-6">
            <div className="flex items-center border-b border-border">
              <button
                onClick={() => setActiveTab('news')}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors duration-200 ${
                  activeTab === 'news' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Newspaper" size={16} />
                <span>News Feed</span>
                <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                  {filteredNews?.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors duration-200 ${
                  activeTab === 'events' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Calendar" size={16} />
                <span>Events Calendar</span>
                <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                  {filteredEvents?.length}
                </span>
              </button>
            </div>

            <div className="p-6">
              {activeTab === 'news' && (
                <div className="space-y-6">
                  {filteredNews?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No News Found</h3>
                      <p className="text-muted-foreground">
                        Try adjusting your filters or search terms to find relevant news articles.
                      </p>
                    </div>
                  ) : (
                    filteredNews?.map((article) => (
                      <NewsCard
                        key={article?.id}
                        article={article}
                        onBookmark={handleBookmark}
                        onShare={handleShare}
                        onViewDetails={handleViewDetails}
                      />
                    ))
                  )}
                </div>
              )}

              {activeTab === 'events' && (
                <div className="space-y-6">
                  {filteredEvents?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Events Found</h3>
                      <p className="text-muted-foreground">
                        Try adjusting your filters or search terms to find relevant events.
                      </p>
                    </div>
                  ) : (
                    filteredEvents?.map((event) => (
                      <EventCard
                        key={event?.id}
                        event={event}
                        onSetReminder={handleSetReminder}
                        onViewDetails={handleViewDetails}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Real-time Status */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-foreground">Live Updates Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="RefreshCw" size={14} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Last updated: {new Date()?.toLocaleTimeString()}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Icon name="Zap" size={14} className="text-accent" />
                  <span className="text-sm text-muted-foreground">
                    {filteredNews?.length + filteredEvents?.length} items loaded
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Icon name="Settings" size={14} />
                  <span>Preferences</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Alert Settings Modal */}
      <AlertSettings
        isOpen={showAlertSettings}
        onClose={() => {
          setShowAlertSettings(false);
          setSelectedAlertSettings(null);
        }}
        onSave={handleSaveAlert}
        initialSettings={selectedAlertSettings}
      />
    </div>
  );
};

export default NewsAndEvents;